import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import api from '../api/client';
import AuthHero from '../components/AuthHero';

/* ────────────────────────────────────────────────────────────────────────────
   «ادعای حساب» برای اولین ورود (پیمانکار / ناظر / مشاور)
   مراحل: تورِ کوتاه (اختیاری) → کد ملی + شمارهٔ قرارداد + سال + موبایل →
   کد تأیید → تعیینِ رمز.
   منطقِ امنیتی سمتِ سرور است؛ این صفحه فقط اطلاعات را جمع می‌کند.
   ──────────────────────────────────────────────────────────────────────────── */

const toEnglishDigits = (v) =>
  String(v || '')
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

const filterAscii = (v) => String(v || '').replace(/[^\x20-\x7E]/g, '');
const toFa = (v) => String(v).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 60%, #e0f2fe 100%)',
    direction: 'rtl',
  },
  card: { maxWidth: 520, width: '100%', borderRadius: 18 },
  content: { padding: theme.spacing(4) },
  title: { fontWeight: 800, color: '#1a237e', marginBottom: theme.spacing(1) },
  hint: { color: '#4a5578', lineHeight: 2, marginBottom: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  btn: { marginTop: theme.spacing(1) },
  link: { marginTop: theme.spacing(2), cursor: 'pointer', color: '#1565c0', textAlign: 'center' },
}));

const TOUR = [
  {
    title: 'این مسیر برای چیست؟',
    body:
      'اگر تاکنون وارد سامانه نشده‌اید، رمزِ عبور ندارید. در این مسیر هویتِ حسابِ خودتان را '
      + 'با اطلاعاتِ یکی از قراردادهایتان تأیید می‌کنید و رمزِ دلخواهتان را می‌سازید.',
  },
  {
    title: 'چه چیزی لازم دارید؟',
    body:
      'کد ملی، شمارهٔ یکی از قراردادهای خودتان (هر کدام از قراردادهایتان قبول است)، '
      + 'سالِ همان قرارداد (مثلاً ۱۴۰۴) و یک شمارهٔ موبایل که در اختیار خودتان است.',
  },
  {
    title: 'بعد از تأیید',
    body:
      'کدِ تأیید به همان شمارهٔ موبایل می‌رود. پس از تأیید، رمزِ جدید می‌گذارید. '
      + 'این شماره به‌عنوانِ «شمارهٔ اولین ورود» ثبت می‌شود و برای بازیابیِ رمز هم '
      + 'همان شماره لازم است.',
  },
];

export default function FirstLogin() {
  const classes = useStyles();
  const [step, setStep] = useState('tour');
  const [tourIndex, setTourIndex] = useState(0);
  const [form, setForm] = useState({ username: '', contractNo: '', year: '', mobile: '' });
  const [claimToken, setClaimToken] = useState('');
  const [otp, setOtp] = useState('');
  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [capsOn, setCapsOn] = useState(false);
  const [langWarn, setLangWarn] = useState(false);

  useEffect(() => {
    if (timer <= 0) return undefined;
    const t = setInterval(() => setTimer((v) => (v <= 1 ? 0 : v - 1)), 1000);
    return () => clearInterval(t);
  }, [timer]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requestClaim = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('users/claim/', {
        username: toEnglishDigits(form.username.trim()),
        contract_number: toEnglishDigits(form.contractNo.trim()),
        contract_year: toEnglishDigits(form.year.trim()),
        mobile: toEnglishDigits(form.mobile.trim()),
      });
      setClaimToken(res.data.claim_token || '');
      setTimer(res.data.expires_in || 300);
      setStep('otp');
    } catch (err) {
      setError(
        (err.response && err.response.data && (err.response.data.error || err.response.data.detail))
          || 'اطلاعات واردشده با سوابق مطابقت ندارد',
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyClaim = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('users/claim/verify/', {
        claim_token: claimToken,
        otp: toEnglishDigits(otp.trim()),
      });
      if (res.data.setup_token) {
        localStorage.setItem('setup_token', res.data.setup_token);
      }
      if (res.data.tokens) {
        localStorage.setItem('access', res.data.tokens.access);
        localStorage.setItem('refresh', res.data.tokens.refresh);
      }
      setStep('password');
    } catch (err) {
      setError(
        (err.response && err.response.data && (err.response.data.error || err.response.data.detail))
          || 'کد تأیید نادرست یا منقضی شده است',
      );
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.password !== pwd.confirm) {
      setError('تکرارِ رمز یکسان نیست');
      return;
    }
    setLoading(true);
    try {
      const setup = localStorage.getItem('setup_token') || '';
      if (setup) {
        api.defaults.headers.common.Authorization = `Bearer ${setup}`;
      }
      const res = await api.post('users/password/set/', {
        password: toEnglishDigits(pwd.password),
        password_confirm: toEnglishDigits(pwd.confirm),
      });
      if (res.data.tokens) {
        localStorage.setItem('access', res.data.tokens.access);
        localStorage.setItem('refresh', res.data.tokens.refresh);
      }
      localStorage.removeItem('setup_token');
      window.location.assign('/');
    } catch (err) {
      setError(
        (err.response && err.response.data && (err.response.data.error || err.response.data.detail))
          || 'تعیینِ رمز ممکن نشد',
      );
    } finally {
      setLoading(false);
    }
  };

  const renderBody = () => {
    if (step === 'tour') {
      const t = TOUR[tourIndex];
      const last = tourIndex === TOUR.length - 1;
      return (
        <>
          <Typography variant="h6" className={classes.title}>{t.title}</Typography>
          <Typography variant="body2" className={classes.hint}>{t.body}</Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box style={{
        marginTop: 14, padding: '10px 12px', background: '#f4f6ff',
        border: '1px solid #d6ddf5', borderRadius: 10,
        direction: 'rtl', textAlign: 'right',
      }}>
        <Typography variant="body2" style={{ color: '#4a5578', lineHeight: 1.9, textAlign: 'right' }}>
          <b>این صفحه فقط برای «اولین ورود» است.</b> اگر قبلاً رمز تعیین کرده‌اید (یا
          درخواست شما با پیام «اطلاعات مطابقت ندارد» رد شد)، از صفحهٔ ورود و تبِ
          <b> «ورود با پیامک»</b> استفاده کنید تا کد به شمارهٔ ثبت‌شده برود.
        </Typography>
        <Button size="small" variant="outlined" style={{ marginTop: 8, fontWeight: 700 }}
          onClick={() => window.location.assign('/login')}>
          رفتن به صفحهٔ ورود (پیامک)
        </Button>
      </Box>

      <Button color="inherit" onClick={() => setStep('form')}>رد کردن</Button>
            <div>
              {tourIndex > 0 && (
                <Button onClick={() => setTourIndex((i) => i - 1)}>قبلی</Button>
              )}
              <Button
                color="primary"
                variant="contained"
                onClick={() => (last ? setStep('form') : setTourIndex((i) => i + 1))}
              >
                {last ? 'شروع' : 'بعدی'}
              </Button>
            </div>
          </Box>
          <Typography variant="caption" style={{ color: '#9aa1c2' }}>
            {`${tourIndex + 1} از ${TOUR.length}`}
          </Typography>
        </>
      );
    }

    if (step === 'form') {
      return (
        <form onSubmit={requestClaim}>
          <Typography variant="h6" className={classes.title}>ادعای حساب</Typography>
          <Typography variant="body2" className={classes.hint}>
            اطلاعات باید با یکی از قراردادهای ثبت‌شده به نامِ همین کد ملی بخواند.
          </Typography>
          <TextField
            fullWidth variant="outlined" label="کد ملی / شناسهٔ ملی"
            className={classes.field} value={form.username}
            onChange={(e) => set('username', e.target.value)} required
          />
          <TextField
            fullWidth variant="outlined" label="شمارهٔ قرارداد"
            className={classes.field} value={form.contractNo}
            onChange={(e) => set('contractNo', e.target.value)} required
          />
          <TextField
            fullWidth variant="outlined" label="سالِ قرارداد (شمسی، مثلاً 1404)"
            className={classes.field} value={form.year}
            onChange={(e) => set('year', e.target.value)} required
          />
          <TextField
            fullWidth variant="outlined" label="شمارهٔ موبایل شما"
            className={classes.field} value={form.mobile}
            onChange={(e) => set('mobile', e.target.value)}
            placeholder="09123456789" required
          />
          <Button
            type="submit" fullWidth color="primary" variant="contained"
            className={classes.btn} disabled={loading}
          >
            {loading ? <CircularProgress size={22} /> : 'ارسال کد تأیید'}
          </Button>
          <Typography className={classes.link} onClick={() => window.location.assign('/login')}>
            بازگشت به صفحهٔ ورود
          </Typography>
        </form>
      );
    }

    if (step === 'otp') {
      return (
        <form onSubmit={verifyClaim}>
          <Typography variant="h6" className={classes.title}>کد تأیید</Typography>
          <Typography variant="body2" className={classes.hint}>
            کدِ ۶ رقمی به شماره‌ای که وارد کردید ارسال شد.
            {timer > 0 ? ` (${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')})` : ''}
          </Typography>
          <TextField
            fullWidth variant="outlined" label="کد تأیید"
            className={classes.field} value={otp}
            onChange={(e) => setOtp(e.target.value)} required
          />
          <Button
            type="submit" fullWidth color="primary" variant="contained"
            className={classes.btn} disabled={loading || !claimToken}
          >
            {loading ? <CircularProgress size={22} /> : 'تأیید'}
          </Button>
          <Typography className={classes.link} onClick={() => setStep('form')}>
            اصلاحِ اطلاعات
          </Typography>
        </form>
      );
    }

    const cats = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/]
      .filter((rx) => rx.test(pwd.password)).length;
    const ascii = pwd.password.length > 0 && /^[\x20-\x7E]*$/.test(pwd.password);
    const lenOk = pwd.password.length >= 10;
    const matchOk = pwd.password.length > 0 && pwd.password === pwd.confirm;
    const score = Math.min(pwd.password.length, 20) + cats * 6 + (ascii ? 3 : 0) + (lenOk ? 6 : 0);
    const pct = Math.max(0, Math.min(100, Math.round((score / 47) * 100)));
    const strengthText = !pwd.password.length ? '—' : pct < 45 ? 'ضعیف' : pct < 75 ? 'متوسط' : 'قوی';
    const barColor = !pwd.password.length ? '#e5e7eb' : pct < 45 ? '#dc2626' : pct < 75 ? '#f59e0b' : '#059669';
    const labelColor = !pwd.password.length ? '#64748b' : pct < 45 ? '#b91c1c' : pct < 75 ? '#b45309' : '#047857';

    return (
      <form onSubmit={savePassword}>
        <Typography variant="h6" className={classes.title}>تعیینِ رمز</Typography>
        <Typography variant="body2" className={classes.hint}>
          رمز شما طبق <b>الزاماتِ افتا</b> ارزیابی می‌شود؛ هر مورد که رعایت شود سبز و تیک‌دار می‌شود.
        </Typography>
        <TextField
          fullWidth variant="outlined" type="password" label="رمز جدید"
          className={classes.field} value={pwd.password} dir="ltr"
          inputProps={{ style: { fontFamily: 'monospace', textAlign: 'left' }, minLength: 10 }}
          onChange={(e) => { const raw = e.target.value; setLangWarn(/[^\\x20-\\x7E]/.test(raw)); setPwd((p) => ({ ...p, password: filterAscii(raw) })); }}
          onKeyDown={(e) => setCapsOn(!!(e.getModifierState && e.getModifierState('CapsLock')))}
          required
        />
        <TextField
          fullWidth variant="outlined" type="password" label="تکرارِ رمز"
          className={classes.field} value={pwd.confirm} dir="ltr"
          inputProps={{ style: { fontFamily: 'monospace', textAlign: 'left' }, minLength: 10 }}
          onChange={(e) => { const raw = e.target.value; setLangWarn(/[^\\x20-\\x7E]/.test(raw)); setPwd((p) => ({ ...p, confirm: filterAscii(raw) })); }}
          onKeyDown={(e) => setCapsOn(!!(e.getModifierState && e.getModifierState('CapsLock')))}
          required
        />
        {langWarn && (
          <MuiAlert severity="warning" style={{ marginBottom: 10 }}>
            صفحه‌کلید را به انگلیسی تغییر دهید؛ رمز فقط با حروف و ارقامِ انگلیسی پذیرفته می‌شود.
          </MuiAlert>
        )}
        {capsOn && (
          <MuiAlert severity="warning" style={{ marginBottom: 10 }}>
            CapsLock روشن است؛ برای امنیتِ رمز آن را خاموش کنید.
          </MuiAlert>
        )}
        <div style={{ background: '#f8fbfc', border: '1px solid #e2eef1', borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
          {[
            [lenOk, `حداقل ۱۰ کاراکتر (شما ${toFa(pwd.password.length)} کاراکتر)`],
            [cats >= 3, `حداقل سه دسته از حروفِ کوچک، بزرگ، رقم و نماد — ${toFa(cats)} از ۴`],
            [ascii, 'فقط حروف و ارقامِ انگلیسی (صفحه‌کلید انگلیسی)'],
            [matchOk, 'تکرارِ رمز با رمزِ اصلی یکسان باشد'],
            [false, 'از رمزهای رایج نباشد (هنگام ثبت بررسی می‌شود)'],
            [false, 'با سه رمزِ آخرِ شما یکسان نباشد (هنگام ثبت بررسی می‌شود)'],
          ].map(([ok, text], i) => (
            <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, lineHeight: 2, color: ok ? '#065f46' : '#64748b', fontWeight: ok ? 800 : 400 }}>
              <span style={{ width: 16, textAlign: 'center', color: ok ? '#059669' : '#cbd5e1' }}>{ok ? '✔' : '○'}</span>
              <span>{text}</span>
            </div>
          ))}
          <div style={{ height: 6, background: '#eef2f7', borderRadius: 999, overflow: 'hidden', marginTop: 8 }}>
            <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: barColor, transition: 'width .2s' }} />
          </div>
          <div style={{ fontSize: 11.5, color: labelColor, marginTop: 4 }}>قدرتِ رمز: {strengthText}</div>
        </div>
        <Button
          type="submit" fullWidth color="primary" variant="contained"
          className={classes.btn} disabled={loading}
        >
          {loading ? <CircularProgress size={22} /> : 'ثبتِ رمز و ورود'}
        </Button>
      </form>
    );
  };

  return (
    <AuthHero
      title="ادعای حساب و اولین ورود"
      subtitle="با کد ملی و شمارهٔ یکی از قراردادهای خودتان، حساب را فعال و رمز تعیین کنید."
    >
      {error && (
        <MuiAlert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>
          {error}
        </MuiAlert>
      )}
      {renderBody()}
    </AuthHero>
  );
}
